// src/features/captions/components/SubAccountCaptionGrantSwitch.tsx

import { useEffect, useState } from 'react';
import { Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { CustomerAllData } from '@/lib/types/Customer';
import { useCaptionsEligibility } from '../useCaptionsEligibility';
import { useSetSubaccountCaptionGrant } from '../useCaptionMutations';

interface SubAccountCaptionGrantSwitchProps {
  subCId: number;
  subAccount: CustomerAllData;
}

/**
 * Parent grant of automated captions to one sub-account. INSTANT-SAVE (like
 * StreamScheduleSection): its own mutation + customerInfo invalidation, never
 * part of the sub-account form's dirty state. Renders nothing unless the
 * parent (the selected master account on this page) is itself entitled —
 * eligibility is the launch gate. The switch holds the toggled value until
 * the refetched customerInfo confirms it, so it never flickers back.
 */
export function SubAccountCaptionGrantSwitch({ subCId, subAccount }: SubAccountCaptionGrantSwitchProps) {
  const { eligible } = useCaptionsEligibility();
  const grantMutation = useSetSubaccountCaptionGrant();

  const serverAllow = subAccount.cfg.quota.features.account.allowAutomatedCaptions ?? false;
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  // Clear the optimistic value once the server state catches up (or reverts).
  useEffect(() => {
    if (pendingValue !== null && serverAllow === pendingValue) {
      setPendingValue(null);
    }
  }, [serverAllow, pendingValue]);

  if (!eligible) return null;

  const handleToggle = async (next: boolean) => {
    setPendingValue(next);
    try {
      await grantMutation.mutateAsync({ childCId: subCId, allow: next });
      notifications.show({
        title: 'Saved',
        message: next
          ? 'Automated captions allowed for this sub-account.'
          : 'Automated captions turned off for this sub-account.',
        color: 'green',
      });
    } catch (err) {
      setPendingValue(null);
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update captions access',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">Captions</Text>
      <Switch
        label="Allow automated captions"
        description="Lets this sub-account turn on live AI captions in its stream settings. Billed per streamed hour. Saves immediately."
        checked={pendingValue ?? serverAllow}
        onChange={(e) => handleToggle(e.currentTarget.checked)}
        disabled={grantMutation.isPending}
      />
    </Stack>
  );
}
